import type { SalaryCalcData, SalaryCalcScheduleBracket } from '../../api/salaryCalc';
import type { CodeValueType } from '../../types/types';
import { SETTLEMENT_TRANSFER_LOOKUP_FAILED } from '../settlementClassification/transferLookup';
import { computeBracketMissing } from './bracketNotice';
import { computeSalary, ComputeSalaryResult, VatPolicy } from './computeSalary';

/**
 * 「전체 강사」 급여표의 판정·합계 — 순수 함수만 둔다(화면·조회 큐와 분리해 시험한다).
 * 숫자는 computeSalary 하나로만 만든다. 표는 편집값 없이 서버 응답값 그대로 넘긴다.
 */

export type SalaryFetchOutcome =
    | { kind: 'ok'; data: SalaryCalcData; integrityErrorCount: number }
    | { kind: 'error'; reason: 'request' | 'transferLookup' | 'emptyBody' | 'invalidNumber' | 'timeout'; status?: number };

/**
 * 성공 판정 — 응답 본문에 급여 객체가 있을 때만 성공.
 * getSalaryCalcs 계열은 400·401·404·500 을 { status } 로 돌려주고(예외 아님), 그 밖의 오류는 던진다.
 * 그래서 「요청이 끝났다」는 성공이 아니다. HTTP 200 이어도 salaryCalcData 가 없으면 실패로 본다.
 */
export const classifySalaryCalcResult = (result: any, thrown?: unknown): SalaryFetchOutcome => {
    if (thrown !== undefined) return { kind: 'error', reason: 'request' };
    const status = result?.status;
    if (status !== 200) {
        if (status === 503 && result?.errorCode === SETTLEMENT_TRANSFER_LOOKUP_FAILED) {
            return { kind: 'error', reason: 'transferLookup', status };
        }
        return { kind: 'error', reason: 'request', status: typeof status === 'number' ? status : undefined };
    }
    const body = result?.data;
    const data = body?.salaryCalcData;
    if (!data || typeof data !== 'object' || Array.isArray(data)) {
        return { kind: 'error', reason: 'emptyBody', status };
    }
    const errors = body?.settlementIntegrityErrors;
    return {
        kind: 'ok',
        data: data as SalaryCalcData,
        integrityErrorCount: Array.isArray(errors) ? errors.length : 0,
    };
};

export type SalaryRowBadge = 'retired' | 'saved' | 'unsaved' | 'bracketMissing' | 'integrity' | 'failed';

/**
 * 퇴사 강사 — 급여 탭 강사 목록(퇴사 포함 조회)의 isDeleted, 또는 이름 끝 「(퇴사)」(정산 화면들이 쓰는 표기).
 * 배지만 붙인다. 금액·합계는 강사별 화면과 같게 둔다(퇴사 후 기본급이 기본값으로 잡히는 문제는 별건).
 */
export const isRetiredCoach = (coach: CodeValueType) =>
    coach?.object?.isDeleted === true || /\(퇴사\)\s*$/.test(coach?.value ?? '');

export type AllCoachesSalaryRow =
    | { coach: CodeValueType; state: 'pending' }
    | { coach: CodeValueType; state: 'failed'; reason: string }
    | {
          coach: CodeValueType;
          state: 'ok';
          calc: ComputeSalaryResult;
          isSavedOnServer: boolean;
          bracketMissing: boolean;
          integrityErrorCount: number;
      };

const NUMBER_KEYS: (keyof ComputeSalaryResult)[] = [
    'settlement',
    'classFee',
    'incentiveKRW',
    'baseSalary',
    'etcBonusKRW',
    'deductionApplied',
    'finalSalary',
];

export const failureReasonText = (reason: string) => {
    switch (reason) {
        case 'transferLookup':
            return '재등록 수업료 확인이 잠시 안 돼요';
        case 'emptyBody':
            return '급여 내역이 비어 있어요';
        case 'invalidNumber':
            return '숫자를 확인할 수 없어요';
        case 'timeout':
            return '응답이 너무 늦어요';
        default:
            return '불러오지 못했어요';
    }
};

export const buildAllCoachesRow = ({
    coach,
    outcome,
    vatPolicy,
    vatRate,
    settlementType,
    fallbackBrackets,
}: {
    coach: CodeValueType;
    outcome: SalaryFetchOutcome | undefined;
    vatPolicy: VatPolicy;
    vatRate: number;
    settlementType: string;
    /** 서버가 구간표를 안 줄 때 강사별 화면이 쓰는 폴백(요율표). 아직 못 받았으면 undefined — 그동안 구간 배지는 보류 */
    fallbackBrackets?: { inScheduleBrackets: SalaryCalcScheduleBracket[]; outScheduleBrackets: SalaryCalcScheduleBracket[] };
}): AllCoachesSalaryRow => {
    if (!outcome) return { coach, state: 'pending' };
    if (outcome.kind === 'error') return { coach, state: 'failed', reason: outcome.reason };

    const data = outcome.data;
    const calc = computeSalary({ data, vatPolicy, vatRate });
    if (NUMBER_KEYS.some((key) => !Number.isFinite(calc[key] as number))) {
        // 서버 값이 비어 NaN 이 되면 0원으로 더하지 않는다
        return { coach, state: 'failed', reason: 'invalidNumber' };
    }

    const serverHasBrackets = !!data.inScheduleBrackets || !!data.outScheduleBrackets;
    let bracketMissing = false;
    if (settlementType === 'tier' && (serverHasBrackets || fallbackBrackets)) {
        bracketMissing = computeBracketMissing({
            data,
            settlementType,
            inScheduleBrackets: data.inScheduleBrackets ?? fallbackBrackets?.inScheduleBrackets ?? [],
            outScheduleBrackets: data.outScheduleBrackets ?? fallbackBrackets?.outScheduleBrackets ?? [],
        }).isMissing;
    }

    return {
        coach,
        state: 'ok',
        calc,
        // 서버는 월 저장 항목이 있으면 그대로 펼치고(하위문서 _id 포함), 없으면 _id 없는 기본 객체를 만든다
        isSavedOnServer: !!data._id,
        bracketMissing,
        integrityErrorCount: outcome.integrityErrorCount,
    };
};

export const rowBadges = (row: AllCoachesSalaryRow): SalaryRowBadge[] => {
    const retired: SalaryRowBadge[] = isRetiredCoach(row.coach) ? ['retired'] : [];
    if (row.state === 'failed') return [...retired, 'failed'];
    if (row.state !== 'ok') return retired;
    const badges: SalaryRowBadge[] = [...retired, row.isSavedOnServer ? 'saved' : 'unsaved'];
    if (row.bracketMissing) badges.push('bracketMissing');
    if (row.integrityErrorCount > 0) badges.push('integrity');
    return badges;
};

/** 모든 숫자가 0이고 경고가 없는 성공 행만 접는다. 실패·대기·경고 행은 0원이어도 접지 않는다. */
export const isFoldableZeroRow = (row: AllCoachesSalaryRow) =>
    row.state === 'ok' &&
    !row.bracketMissing &&
    row.integrityErrorCount === 0 &&
    NUMBER_KEYS.every((key) => row.calc[key] === 0);

export type AllCoachesSalaryTotals = {
    settlement: number;
    classFee: number;
    incentiveKRW: number;
    baseSalary: number;
    etcBonusKRW: number;
    deductionApplied: number;
    finalSalary: number;
    includedCount: number;
    failedCount: number;
    pendingCount: number;
    integrityCount: number;
};

/** 합계 — 성공 행만 더한다. 불러오지 못한 강사는 0원으로 더하지 않고 수만 센다. */
export const summarizeAllCoachesRows = (rows: AllCoachesSalaryRow[]): AllCoachesSalaryTotals => {
    const totals: AllCoachesSalaryTotals = {
        settlement: 0,
        classFee: 0,
        incentiveKRW: 0,
        baseSalary: 0,
        etcBonusKRW: 0,
        deductionApplied: 0,
        finalSalary: 0,
        includedCount: 0,
        failedCount: 0,
        pendingCount: 0,
        integrityCount: 0,
    };
    rows.forEach((row) => {
        if (row.state === 'pending') {
            totals.pendingCount += 1;
            return;
        }
        if (row.state === 'failed') {
            totals.failedCount += 1;
            return;
        }
        totals.includedCount += 1;
        if (row.integrityErrorCount > 0) totals.integrityCount += 1;
        totals.settlement += row.calc.settlement;
        totals.classFee += row.calc.classFee;
        totals.incentiveKRW += row.calc.incentiveKRW;
        totals.baseSalary += row.calc.baseSalary;
        totals.etcBonusKRW += row.calc.etcBonusKRW;
        totals.deductionApplied += row.calc.deductionApplied;
        totals.finalSalary += row.calc.finalSalary;
    });
    return totals;
};

/** 표에 올릴 강사 — 급여 탭 강사 고르기와 같은 목록. 'all' 같은 묶음 항목은 뺀다. */
export const salaryTableCoaches = (coachList: CodeValueType[] | undefined) =>
    (coachList ?? []).filter((coach) => !!coach?.code && coach.code !== 'all');
